import React from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';

/**
 * Capitalizes the first letter of each word in a string.
 * @param {string} str - Input string.
 * @returns {string} - Capitalized string.
 */
export function capitalizeWords(str) {
    return str
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/**
 * Renders sanitized HTML content in the specified tag.
 * @param {Object} props - Component props.
 * @param {string} props.html - Raw HTML string.
 * @param {string} [props.tag='p'] - HTML tag to use for rendering.
 * @param {string} [props.className] - Optional CSS class.
 */
export function SanitizedHTML({ html, tag = 'p', className = '' }) {
    const sanitizedHTML = DOMPurify.sanitize(html);

    const Tag = ['div', 'span', 'h1', 'p'].includes( tag.toLowerCase() ) ? tag : 'p';

    return <Tag className={className} dangerouslySetInnerHTML={{ __html: sanitizedHTML }} />;
}

SanitizedHTML.propTypes = {
    html: PropTypes.string.isRequired,
    tag: PropTypes.string,
    className: PropTypes.string
};

/**
 * Renders a mailto link with optional subject and body.
 * @param {Object} props - Component props.
 * @param {string} props.email - Email address.
 * @param {string} [props.subject] - Email subject.
 * @param {string} [props.body] - Email body.
 * @param {React.ReactNode} props.children - Link content.
 */
export function Mailto({ email, subject = '', body = '', children, ...rest }) {
    const mailtoHref = `mailto:${email}?subject=${encodeURIComponent( subject )}&body=${encodeURIComponent( body )}`;

    return (
        <a href={mailtoHref} className="text-dark" {...rest}>
            {children}
        </a>
    );
}

Mailto.propTypes = {
    email: PropTypes.string.isRequired,
    subject: PropTypes.string,
    body: PropTypes.string,
    children: PropTypes.node.isRequired
};